import { z } from 'zod';
/**
 * Money is always represented as an integer number of cents (sen) to avoid
 * floating point rounding errors. 1 MYR = 100 cents.
 */
export const MoneyCentsSchema = z.number().int();
function groupThousands(digits) {
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
/**
 * Formats an integer number of cents as a Malaysian Ringgit string,
 * e.g. formatRM(1250) => "RM 12.50".
 */
export function formatRM(cents) {
    const rounded = Math.round(cents);
    const sign = rounded < 0 ? '-' : '';
    const abs = Math.abs(rounded);
    const whole = Math.floor(abs / 100);
    const frac = (abs % 100).toString().padStart(2, '0');
    return `${sign}RM ${groupThousands(whole.toString())}.${frac}`;
}
/**
 * Parses a user-entered Ringgit amount into an integer number of cents.
 * Accepts an optional "RM" prefix and optional whitespace, e.g.
 * "12", "12.5", "RM 12.50". Rejects negative amounts and non-numeric
 * input, returning null in those cases. Values with more than two decimal
 * places are rounded to the nearest cent.
 *
 * A comma is accepted either as a decimal separator ("12,50" => 1250) or,
 * when there are multiple commas or a comma followed later by a dot, as a
 * thousands separator ("1,234.50" => 123450). Anything else involving a
 * comma (e.g. "1,2,3") is rejected.
 */
export function parseRM(input) {
    const match = /^\s*(?:RM)?\s*(-?[\d,]+(?:\.\d+)?)\s*$/i.exec(input);
    if (!match?.[1])
        return null;
    const raw = match[1];
    const commaCount = (raw.match(/,/g) ?? []).length;
    let normalised;
    if (commaCount === 0) {
        normalised = raw;
    }
    else if (commaCount === 1 && !raw.includes('.')) {
        // A single comma with no dot is a decimal separator: "12,50" => "12.50".
        normalised = raw.replace(',', '.');
    }
    else {
        // Multiple commas, or a comma alongside a dot, must be thousands
        // separators grouped in threes ahead of any decimal point, e.g.
        // "1,234.50" or "1,234,567". Anything else (e.g. "1,2,3") is invalid.
        const thousandsMatch = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/.exec(raw);
        if (!thousandsMatch)
            return null;
        normalised = raw.replace(/,/g, '');
    }
    const value = Number(normalised);
    if (!Number.isFinite(value) || value < 0)
        return null;
    return Math.round(value * 100);
}
//# sourceMappingURL=money.js.map